import * as React from 'react';
import { styled } from '@linaria/react';
import { Redacted_Script } from 'next/font/google';

import useBoundingBox from '@/hooks/use-bounding-box';
import { normalize } from '@/utils';

import Demo, { DemoSliderControl } from '@/components/Demo';
import Spacer from '@/components/Spacer';
import Spinner from '@/components/Spinner';
import ArrowShowingDistance from '@/components/ArrowShowingDistance';

const font = Redacted_Script({
  subsets: ['latin'],
  weight: ['400', '700'],
  display: 'block',
});

const MIN_WIDTH = 120;
const MAX_WIDTH = 440;

interface Props {
  type: 'showing-metadata-width' | 'showing-metadata-layout';
}

function EmailLayoutDemo({ type }: Props) {
  const ref = React.useRef<HTMLDivElement>(null);
  const boundingBox = useBoundingBox(ref);

  return (
    <Demo
      ref={ref}
      initialValues={{
        width: 100,
      }}
      controlPosition="top"
      outputWrapper={null}
      customGrid={{
        gridTemplateColumns: `min(${MAX_WIDTH}px, 100%)`,
        justifyContent: 'center',
      }}
      controls={[
        <DemoSliderControl
          id="width"
          key="width"
          name="width"
          label="Window Width"
          min={1}
          max={100}
          previewRenderer={() => null}
        />,
      ]}
    >
      {({ width }) => {
        // In `showing-metadata-width` mode, the ArrowShowingDistance sits above the container. Because it's not in flow, we need to add space for it using margin:
        const outputMarginTop =
          type === 'showing-metadata-width' ? 48 : 8;

        if (!boundingBox) {
          return (
            <Spacer
              axis="vertical"
              size={
                // HACK: I’m dynamically calculating the height for the showing-metadata-width variant, but I’m too lazy to do that for the other one. Using a magic number.
                type === 'showing-metadata-width'
                  ? `calc(15rem + 16px + ${outputMarginTop}px)`
                  : '301px'
              }
            >
              <Spinner />
            </Spacer>
          );
        }

        const maxContainerWidth = Math.min(
          boundingBox.width,
          MAX_WIDTH
        );

        const containerWidth = Math.round(
          normalize(width, 1, 100, MIN_WIDTH, maxContainerWidth)
        );

        const breakpoint = window.innerWidth > 600 ? 250 : 250;

        let isTwoColumnLayout = containerWidth >= breakpoint;

        // Subtract padding (and gap for desktop):
        const mobileAvailableSpace = containerWidth - 8 * 2;
        const desktopAvailableSpace = mobileAvailableSpace - 8;

        const metadataWidth = isTwoColumnLayout
          ? Math.round(desktopAvailableSpace * 0.42)
          : mobileAvailableSpace;
        const bodyWidth = isTwoColumnLayout
          ? desktopAvailableSpace - metadataWidth
          : mobileAvailableSpace;

        let metadataLayout = 'sparse';

        if (type === 'showing-metadata-layout') {
          metadataLayout =
            metadataWidth > 150 ? 'sparse' : 'condensed';
        }

        return (
          <>
            {type === 'showing-metadata-layout' && (
              <MetadataLayoutIndicator>
                <LayoutValue
                  data-matched={metadataLayout === 'sparse'}
                >
                  Sparse
                </LayoutValue>
                <LayoutValue
                  data-matched={metadataLayout === 'condensed'}
                >
                  Condensed
                </LayoutValue>
              </MetadataLayoutIndicator>
            )}
            <OutputWrapper
              style={{
                width: containerWidth,

                marginTop: outputMarginTop,
              }}
            >
              <MetadataWrapper
                style={{
                  width: metadataWidth,
                  minWidth: metadataWidth,
                }}
              >
                {type === 'showing-metadata-width' && (
                  <WidthIndicator>
                    <ArrowShowingDistance
                      axis="horizontal"
                      unit="rem"
                      flipTextSide
                      distance={metadataWidth}
                      multiple={2}
                    />
                  </WidthIndicator>
                )}
                <Metadata
                  className={font.className}
                  data-layout={metadataLayout}
                >
                  <Header>Subject line here</Header>
                  <Line>
                    <Key>From:</Key>
                    <Value>Josh Comeau</Value>
                  </Line>
                  <Line>
                    <Key>Reply:</Key>
                    <Value>Josh@email.com</Value>
                  </Line>
                  <Line>
                    <Key>Date:</Key>
                    <Value>January 1</Value>
                  </Line>
                </Metadata>
              </MetadataWrapper>
              <Body
                style={{ width: bodyWidth, minWidth: bodyWidth }}
                className={font.className}
              >
                <p>
                  Hi, screen-reader users! This is a scribble intended
                  to represent text in an email.
                </p>
                <p>You can ignore all text in this container.</p>
              </Body>
            </OutputWrapper>
          </>
        );
      }}
    </Demo>
  );
}

const OutputWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-inline: auto;
  outline: 1px solid hsl(270deg 60% 80% / 0.3);
  border-radius: 8px;
  padding: 8px;
  background: hsl(240deg 40% 20%);
`;

const MetadataLayoutIndicator = styled.div`
  display: flex;
  justify-content: center;
`;

const LayoutValue = styled.div`
  width: 5rem;
  color: var(--color-gray-400);
  text-align: center;
  font-size: 0.875rem;
  animation:
    glowYellow 1500ms cubic-bezier(0.88, -0.1, 0.39, 0.99),
    jump 1000ms;

  @keyframes jump {
    from {
      transform: scale(1.2);
    }
  }
  @keyframes glowYellow {
    from {
      color: hsl(50deg 100% 50%);
    }
  }

  &[data-matched='true'] {
    font-weight: var(--font-weight-medium);
    color: white;
  }
  &[data-matched='false'] {
    animation: none;
  }
`;
const MetadataWrapper = styled.div`
  position: relative;
  min-width: 0;
`;

const WidthIndicator = styled.div`
  position: absolute;
  top: -16px;
  left: 0;
  right: 0;
  transform: translateY(-100%);
`;
const Metadata = styled.div`
  border: 1px solid hsl(270deg 60% 80%);
  padding: 4px;
  border-radius: 4px;
  font-size: 0.875rem;
`;
const Header = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;
const Line = styled.div`
  display: flex;
  gap: 16px;

  ${Metadata}[data-layout='condensed'] & {
    flex-direction: column;
    gap: 0;
  }
`;
const Key = styled.span`
  display: block;
  color: hsl(270deg 60% 80%);
  font-size: 0.75rem;
  width: 25%;

  ${Metadata}[data-layout='condensed'] & {
    margin-bottom: -4px;
  }
`;
const Value = styled.span`
  flex: 1;
  display: block;
  color: white;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${Metadata}[data-layout='condensed'] & {
    margin-bottom: 8px;
  }
`;

const Body = styled.div`
  --selection-background-color: hsl(50deg 100% 70%);
  --selection-text-color: black;
  width: 100%;
  height: 15rem;
  padding: 8px;
  background: white;
  color: hsl(270deg 10% 50%);
  border-radius: 4px;
  /*
    The lines look too spread-apart, since the scribbles occupy a very small part of the text box.
    NOTE: in any other context, this would be an accessibility violation, but in this specific case, the text is scribbles and not intended to be read.
  */
  line-height: 1.2;
  overflow: hidden;

  & p:not(:last-child) {
    margin-bottom: 8px;
  }
`;

export default EmailLayoutDemo;
